import { useCallback, useMemo, useState } from 'react'

import type { ParagraphFeedback } from './content-brief.interface'
import { type ParsedBlock, parseBlockList } from './content-brief-parser.helper'
import { type CommentBlockMeta, useContentBriefComments } from './use-content-brief-comments.hook'

export interface UseContentBriefContentStateProps {
  content: string
  isHtml: boolean
  onProceed: (feedbackList: ParagraphFeedback[], additionalComment: string) => void
  onCopy: () => void
}

export interface UseContentBriefContentStateReturn {
  markdownBlockList: ParsedBlock[]
  commentMap: Record<number, string>
  commentCount: number
  feedbackList: ParagraphFeedback[]
  handleCommentChange: (blockIndex: number, comment: string | undefined) => void
  handleShadowBlocksReady: (blocks: { lineNumber: number; text: string }[]) => void
  handleProceedConfirm: (additionalComment: string) => void
  handleCopy: () => void
}

export const useContentBriefContentState = ({
  content,
  isHtml,
  onProceed,
  onCopy,
}: UseContentBriefContentStateProps): UseContentBriefContentStateReturn => {
  const [shadowBlockList, setShadowBlockList] = useState<CommentBlockMeta[]>([])

  const markdownBlockList = useMemo(
    () => (isHtml ? [] : parseBlockList(content)),
    [content, isHtml]
  )
  const blockList = isHtml ? shadowBlockList : markdownBlockList

  const { commentMap, feedbackList, commentCount, handleCommentChange, resetComments } =
    useContentBriefComments({ blockList })

  const handleShadowBlocksReady = useCallback(
    (blocks: { lineNumber: number; text: string }[]): void => {
      setShadowBlockList(blocks.map((b) => ({ index: b.lineNumber - 1, textContent: b.text })))
    },
    []
  )

  const handleProceedConfirm = useCallback(
    (additionalComment: string): void => {
      onProceed(feedbackList, additionalComment)
      resetComments()
    },
    [feedbackList, onProceed, resetComments]
  )

  const handleCopy = useCallback((): void => {
    navigator.clipboard.writeText(content)
    onCopy()
  }, [content, onCopy])

  return {
    markdownBlockList,
    commentMap,
    commentCount,
    feedbackList,
    handleCommentChange,
    handleShadowBlocksReady,
    handleProceedConfirm,
    handleCopy,
  }
}
